<template>
    <div ref="chartDiv" style="width: 100%; height: 260px"></div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

export interface ITrendDataItem {
    day: string;
    new_users: number;
    verified_emails: number;
    new_registration_teams: number;
    new_standalone_teams: number;
    new_free_plans: number;
    free_plan_restarts: number;
    new_subscriptions: number;
    expired_trials: number;
    trial_conversions: number;
    yearly_subscriptions: number;
}

type ChartTab = "activity" | "users" | "plans";
export type ChartView = "trend" | "day";

interface SeriesEntry {
    name: string;
    field: string;
    color: string;
}

interface DayBarItem {
    label: string;
    value: number;
    color: string;
}

const props = defineProps<{
    chartData: ITrendDataItem[];
    activeTab: ChartTab;
    activeView: ChartView;
    selectedDate: string;
}>();

const chartDiv = ref<HTMLElement | null>(null);
let root: am5.Root | null = null;

const getSeriesConfig = (): SeriesEntry[] => {
    if (props.activeTab === "users") {
        return [
            {
                name: t("vue.kiosk_da_new_registrations"),
                field: "new_users",
                color: "#3B82F6",
            },
            {
                name: t("vue.kiosk_da_email_verifications"),
                field: "verified_emails",
                color: "#10B981",
            },
            {
                name: t("vue.kiosk_da_new_registration_teams"),
                field: "new_registration_teams",
                color: "#A78BFA",
            },
        ];
    }

    if (props.activeTab === "plans") {
        return [
            {
                name: t("vue.kiosk_da_new_free_plans"),
                field: "new_free_plans",
                color: "#F97316",
            },
            {
                name: t("vue.kiosk_da_free_plan_restarts"),
                field: "free_plan_restarts",
                color: "#F59E0B",
            },
            {
                name: t("vue.kiosk_da_new_subscriptions"),
                field: "new_subscriptions",
                color: "#059669",
            },
            {
                name: t("vue.kiosk_da_yearly_subscriptions"),
                field: "yearly_subscriptions",
                color: "#0D9488",
            },
            {
                name: t("vue.kiosk_da_expired_trials"),
                field: "expired_trials",
                color: "#EF4444",
            },
        ];
    }

    return [
        {
            name: t("vue.kiosk_da_new_registrations"),
            field: "new_users",
            color: "#3B82F6",
        },
        {
            name: t("vue.kiosk_da_email_verifications"),
            field: "verified_emails",
            color: "#10B981",
        },
        {
            name: t("vue.kiosk_da_new_standalone_teams"),
            field: "new_standalone_teams",
            color: "#8B5CF6",
        },
        {
            name: t("vue.kiosk_da_new_free_plans"),
            field: "new_free_plans",
            color: "#F97316",
        },
        {
            name: t("vue.kiosk_da_new_subscriptions"),
            field: "new_subscriptions",
            color: "#059669",
        },
        {
            name: t("vue.kiosk_da_yearly_subscriptions"),
            field: "yearly_subscriptions",
            color: "#0D9488",
        },
        {
            name: t("vue.kiosk_da_expired_trials"),
            field: "expired_trials",
            color: "#EF4444",
        },
    ];
};

const disposeRoot = (): void => {
    if (root) {
        root.dispose();
        root = null;
    }
};

const styleTooltip = (
    tooltip: am5.Tooltip,
    bg: string,
    text = "#FFFFFF",
): void => {
    // Disable amCharts' automatic luminance-based text color so our explicit
    // label fill is always respected (otherwise text can render dark-on-dark).
    tooltip.setAll({
        autoTextColor: false,
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 9,
        paddingRight: 9,
    });
    tooltip.get("background")?.setAll({
        fill: am5.color(bg),
        fillOpacity: 0.96,
        stroke: am5.color(bg),
        strokeOpacity: 0,
    });
    tooltip.label.setAll({
        fill: am5.color(text),
        fontSize: 12,
        fontWeight: "600",
    });
};

const buildTrendChart = (): void => {
    if (!chartDiv.value) return;
    disposeRoot();

    const newRoot = am5.Root.new(chartDiv.value);
    root = newRoot;
    newRoot.setThemes([am5.Theme.new(newRoot)]);
    newRoot._logo.dispose();

    const chart = newRoot.container.children.push(
        am5xy.XYChart.new(newRoot, {
            layout: newRoot.verticalLayout,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 0,
            paddingRight: 0,
        }),
    );

    chart.set("cursor", am5xy.XYCursor.new(newRoot, { behavior: "none" }));

    const xRenderer = am5xy.AxisRendererX.new(newRoot, {
        minGridDistance: 36,
    });
    xRenderer.labels.template.setAll({
        fill: am5.color("#9CA3AF"),
        fontSize: 11,
    });
    xRenderer.grid.template.setAll({ stroke: am5.color("#F3F4F6") });

    const axisTooltip = am5.Tooltip.new(newRoot, {});
    styleTooltip(axisTooltip, "#1F2937");

    const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(newRoot, {
            categoryField: "label",
            renderer: xRenderer,
            tooltip: axisTooltip,
        }),
    );

    const yRenderer = am5xy.AxisRendererY.new(newRoot, {});
    yRenderer.labels.template.setAll({
        fill: am5.color("#9CA3AF"),
        fontSize: 11,
    });
    yRenderer.grid.template.setAll({ stroke: am5.color("#F3F4F6") });

    const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(newRoot, {
            renderer: yRenderer,
            min: 0,
            strictMinMax: true,
        }),
    );

    const data = props.chartData.map((item) => ({
        ...item,
        label: new Date(item.day + "T00:00:00").toLocaleDateString("cs-CZ", {
            month: "numeric",
            day: "numeric",
        }),
        _isSelected: item.day === props.selectedDate,
    }));

    getSeriesConfig().forEach(({ name, field, color }) => {
        const seriesTooltip = am5.Tooltip.new(newRoot, {
            labelText: "{name}: {valueY}",
        });
        styleTooltip(seriesTooltip, color);

        const series = chart.series.push(
            am5xy.ColumnSeries.new(newRoot, {
                name,
                xAxis,
                yAxis,
                valueYField: field,
                categoryXField: "label",
                stacked: true,
                tooltip: seriesTooltip,
                fill: am5.color(color),
                stroke: am5.color(color),
            }),
        );

        series.columns.template.setAll({
            fillOpacity: 0.85,
            strokeOpacity: 0,
            cornerRadiusTL: 2,
            cornerRadiusTR: 2,
            width: am5.percent(70),
        });

        series.columns.template.adapters.add(
            "fillOpacity",
            (opacity, target) => {
                const ctx = target.dataItem?.dataContext as
                    | { _isSelected?: boolean }
                    | undefined;
                return ctx?._isSelected ? 1 : (opacity ?? 0.85);
            },
        );

        series.data.setAll(data);
    });

    xAxis.data.setAll(data);
};

const buildDayChart = (): void => {
    if (!chartDiv.value) return;
    disposeRoot();

    const dayItem = props.chartData.find((d) => d.day === props.selectedDate);
    if (!dayItem) return;

    const seriesConfig = getSeriesConfig();
    const barData: DayBarItem[] = seriesConfig.map((s) => ({
        label: s.name,
        value: (dayItem[s.field as keyof ITrendDataItem] as number) ?? 0,
        color: s.color,
    }));

    const newRoot = am5.Root.new(chartDiv.value);
    root = newRoot;
    newRoot.setThemes([am5.Theme.new(newRoot)]);
    newRoot._logo.dispose();

    const chart = newRoot.container.children.push(
        am5xy.XYChart.new(newRoot, {
            layout: newRoot.verticalLayout,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 0,
            paddingRight: 0,
        }),
    );

    chart.set("cursor", am5xy.XYCursor.new(newRoot, { behavior: "none" }));

    const xRenderer = am5xy.AxisRendererX.new(newRoot, {
        minGridDistance: 40,
    });
    xRenderer.labels.template.setAll({
        fill: am5.color("#9CA3AF"),
        fontSize: 11,
        maxWidth: 90,
        oversizedBehavior: "wrap",
        textAlign: "center",
    });
    xRenderer.grid.template.setAll({ stroke: am5.color("#F3F4F6") });

    const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(newRoot, {
            categoryField: "label",
            renderer: xRenderer,
        }),
    );

    const yRenderer = am5xy.AxisRendererY.new(newRoot, {});
    yRenderer.labels.template.setAll({
        fill: am5.color("#9CA3AF"),
        fontSize: 11,
    });
    yRenderer.grid.template.setAll({ stroke: am5.color("#F3F4F6") });

    const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(newRoot, {
            renderer: yRenderer,
            min: 0,
            strictMinMax: true,
        }),
    );

    const dayTooltip = am5.Tooltip.new(newRoot, {
        labelText: "{categoryX}: {valueY}",
        getFillFromSprite: false,
    });
    styleTooltip(dayTooltip, "#1F2937");

    const series = chart.series.push(
        am5xy.ColumnSeries.new(newRoot, {
            xAxis,
            yAxis,
            valueYField: "value",
            categoryXField: "label",
            tooltip: dayTooltip,
        }),
    );

    series.columns.template.setAll({
        fillOpacity: 0.9,
        strokeOpacity: 0,
        cornerRadiusTL: 3,
        cornerRadiusTR: 3,
        width: am5.percent(55),
    });

    series.columns.template.adapters.add("fill", (fill, target) => {
        const idx = target.dataItem?.index;
        return idx !== undefined
            ? am5.color(barData[idx]?.color ?? "#9CA3AF")
            : fill;
    });
    series.columns.template.adapters.add("stroke", (stroke, target) => {
        const idx = target.dataItem?.index;
        return idx !== undefined
            ? am5.color(barData[idx]?.color ?? "#9CA3AF")
            : stroke;
    });

    series.data.setAll(barData);
    xAxis.data.setAll(barData);
};

const buildChart = (): void => {
    if (props.activeView === "day") {
        buildDayChart();
    } else {
        buildTrendChart();
    }
};

onMounted(() => {
    buildChart();
});

watch(
    () => [props.activeTab, props.activeView, props.selectedDate] as const,
    () => {
        buildChart();
    },
);

onUnmounted(() => {
    disposeRoot();
});
</script>
